<template>
  <UiModal
    v-model="isVisible"
  >
    <UiModalHeader
      title="Deposit Funds"
      @dismiss="isVisible = false"
    />

    <div class="py-3 px-4">
      <div class="py-2 px-3 mb-4 text-sm bg-gray-700 rounded-lg">
        You can also deposit by sending assets to the following address:
        <div class="font-mono">
          <AddressDisplay :address="privateKey.address" />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <UiInputGroup label="Type">
            <UiSelectCustom
              v-model="type"
              button-class="w-full text-left"
              :options="[
                { id: 'ETH', name: 'ETH' },
                { id: 'ERC20', name: 'ERC20' }
              ]"
            />
          </UiInputGroup>
        </div>

        <div>
          <UiInputGroup
            v-if="type === 'ERC20'"
            label="Contract"
          >
            <UiSelectCustom
              v-model="contract"
              button-class="w-full text-left"
              :options="[
                { name: 'Wrapped ETH', id: '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2' },
                { name: 'Blur Pool Token', id: '0x0000000000a39bb272e79075ade125fd351887ac' },
              ]"
            />
          </UiInputGroup>
        </div>
      </div>

      <UiInputGroup label="Amount">
        <UiInput v-model="amount" />

        <div
          class="flex gap-1 items-center text-sm text-gray-400 hover:text-gray-200 cursor-pointer"
          @click="amount = availableBalance"
        >
          Current balance: <span v-if="!isLoading">{{ availableBalance }}</span>

          <span
            v-if="isLoading"
            class="inline-block w-12 h-4 bg-gray-700 rounded animate-pulse"
          />
        </div>
      </UiInputGroup>
    </div>

    <UiModalFooter>
      <UiButton
        :is-loading="isDepositing"
        @click="deposit"
      >
        Deposit
      </UiButton>
    </UiModalFooter>
  </UiModal>
</template>

<script setup>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { ethers } from 'ethers';
import gql from 'graphql-tag';
import { computed, toRefs, ref } from 'vue';
import { useStore } from 'vuex';
import Web3 from 'web3';
import AddressDisplay from '../AddressDisplay.vue';
import UiButton from '../ui/UiButton.vue';
import UiInput from '../ui/UiInput.vue';
import UiInputGroup from '../ui/UiInputGroup.vue';
import UiModal from '../ui/UiModal.vue';
import UiModalFooter from '../ui/UiModalFooter.vue';
import UiModalHeader from '../ui/UiModalHeader.vue';
import UiSelectCustom from '../ui/UiSelectCustom.vue';

const abi = [
  // Read-Only Functions
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',

  // Authenticated Functions
  'function transfer(address to, uint amount) returns (bool)',

  // Events
  'event Transfer(address indexed from, address indexed to, uint amount)',
];

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  privateKey: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const { modelValue, privateKey } = toRefs(props);

const isVisible = computed({
  get: () => modelValue.value,
  set: (value) => emit('update:modelValue', value),
});

const type = ref('ETH');
const amount = ref(0);
const contract = ref('');
const { result: ethBalance, loading: isLoadingEthBalance } = useQuery(gql`
    query GetBalance($id: String!) {
      wallet(where: {id: $id}) {
        balance: ethBalance
      }
    }
  `, () => ({
  id: privateKey.value.address,
}), () => ({
  enabled: isVisible.value,
}));

const { result: erc20Balance, loading: isLoadingErc20Balance } = useQuery(gql`
    query GetErc20Balance($id: String!, $contract: String!) {
      wallet(where: {id: $id}) {
        balance: erc20Balance(contract: $contract)
      }
    }
  `, () => ({
  id: privateKey.value.address,
  contract: contract.value,
}), () => ({
  enabled: isVisible.value && type.value === 'ERC20' && contract.value?.length,
}));

const availableBalance = computed(() => {
  if (type.value === 'ETH') {
    return ethBalance.value?.wallet?.balance;
  }

  return erc20Balance.value?.wallet?.balance;
});

const isLoading = computed(() => isLoadingEthBalance.value || isLoadingErc20Balance.value);

const isDepositing = ref(false);

const destination = ref('');

const { mutate } = useMutation(gql`
mutation WithdrawFunds(
  $privateKeyId: Int!
  $type: String!
  $contract: String
  $amount: Float!
  $destination: String!
) {
  transferWalletFunds(
    privateKeyId: $privateKeyId
    type: $type
    contract: $contract
    amount: $amount
    destination: $destination
  )
}`);

const store = useStore();

const deposit = async () => {
  isDepositing.value = true;

  const provider = await store.dispatch('auth/getProvider');

  const ethersProvider = new ethers.providers.Web3Provider(provider);
  const signer = ethersProvider.getSigner();
  if (type.value === 'ETH') {
    const amountInWei = ethers.utils.parseEther(amount.value);
    try {
      const tx = await signer.sendTransaction({
        to: privateKey.value.address,
        value: amountInWei,
      });
    } catch (e) {
      console.log('error', e.message);
    } finally {
      isDepositing.value = false;
    }
  }

  if (type.value === 'ERC20') {
    const erc20 = new ethers.Contract(contract.value, abi, signer);
    const decimals = await erc20.decimals();
    const amountInWei = ethers.utils.parseUnits(amount.value, decimals);

    try {
      const tx = await erc20.transfer(privateKey.value.address, amountInWei);

      console.log('got tx', tx);
    } catch (e) {
      console.log('error', e.message);
    } finally {
      isDepositing.value = false;
    }
  }
};
</script>
